import { useState, FC, useEffect, useContext } from 'react'
import classNames from 'classnames'
import {
  CompatRouter,
  Navigate,
  useNavigationType,
  useLocation,
  Route,
  Routes,
} from 'react-router-dom-v5-compat'
import Router from 'shared/universal-router'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import Image from 'components/image'
import ScrollToTop from 'components/scroll-to-top'
import {
  registryFromIdPath,
  registryHomePath as baseRegistryHomePath,
  loginPath,
} from 'lib/urls'
import { REGISTRY_ROLES } from 'registry/constants'
import Loader from './ServerRenderLoader/ServerRenderLoader'
import SlideTransition from './slide-transition/slide-transition'
import PathToParenthood from './slides/path-to-parenthood'
import Signup from './slides/Signup/Signup'
import AddItemsValueProposition from './slides/add-items-value-proposition'
import AddButton from './slides/AddButton'
import AddChromeExtension from './slides/AddChromeExtension/AddChromeExtension'
import InstallApp from './slides/install-app'
import MobileAddItemsExperience from './slides/mobile-add-items-experience'
import InstallExtension from './slides/InstallExtension/InstallExtension'
import Sentiment from './slides/Sentiment/sentiment'
import FirstTimeParentSlide from './slides/FirstTimeParentSlide/first-time-parent-slide'
import AcceptPartnerInvite from './slides/AcceptPartnerInvite'
import { AddPartner } from './slides/AddPartner/AddPartner'
import PostInstallExtension from './slides/PostInstallExtension/PostInstallExtension'
import { AddExperiencePrimer } from './slides/AddExperiencePrimer/AddExperiencePrimer'
import useFeatureFlag from 'shared/hooks/useFeatureFlag/useFeatureFlag'
import { browserName } from 'lib/bowser'

import css from './OnboardingWizard.styles.scss'
import {
  ONBOARDING_STEPS,
  ONBOARDING_STEP_PATHS,
  PATHS,
  HIDE_HEADER_PATHS,
  SHOW_BACKGROUND_PATHS,
  SHOW_PLAIN_BACKGROUND_PATHS,
} from './constants'
import OnboardingAppContext from './OnboardingAppContext'
import { useDispatch } from 'react-redux'
import { setRegistry, setUser } from 'registry/actions'

interface OnboardingWizardProps {
  initialPath: string
  currentRegistryRole?: string
  onboardingFlow: string
  actEx2bMobile: string
  ssoEnabled: boolean
  registry?: {
    id: number
    firstNames: string
  }
}

const OnboardingWizard: FC<OnboardingWizardProps> = ({
  initialPath,
  currentRegistryRole,
  onboardingFlow,
  actEx2bMobile,
  ssoEnabled,
  registry,
}) => {
  const dispatch = useDispatch()
  const [currentUser] = useCurrentUser()
  const {
    authToken,
    deviceSupportsBabylistApp,
    showExtensionInstall,
    skipPassword,
  } = useContext(OnboardingAppContext)

  // START Experiments
  // Check here for updated experiment membership for page reloads
  const isInActEx2bMobileVariation =
    actEx2bMobile === 'variation1' || actEx2bMobile === 'variation2'
  const isInDayZeroDesktopExtensionExperiment =
    browserName === 'Chrome' &&
    useFeatureFlag('24bExDayzeroDesktopExtensionV2', 'control', {
      logUsage: true,
    }).flagValue === 'variation1'

  const showExtensionPage = showExtensionInstall

  // END Experiments

  const [currentPathname, setCurrentPathname] = useState(
    initialPath.replace('/registry/new', '') || '/'
  )
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState<number>(
    ONBOARDING_STEPS.BEFORE_LOADING
  )
  useEffect(() => {
    const newStep = ONBOARDING_STEP_PATHS[currentPathname] || 0

    if (newStep === 1 && !currentUser) {
      // Any time the user lands on the first step of onboarding,
      // And they aren't logged in,
      // Reset user and registry data to defaults
      dispatch(setUser({}))
      dispatch(setRegistry({ uiState: {} }))
    }

    if (currentOnboardingStep === ONBOARDING_STEPS.BEFORE_LOADING) {
      // Initial step won't animate if we don't wait a bit
      setTimeout(() => {
        setCurrentOnboardingStep(newStep)
      }, 100)
      return
    }

    if (currentOnboardingStep !== newStep) {
      setCurrentOnboardingStep(newStep)
    }
  }, [currentPathname])

  const isCollaboratorActivation =
    currentRegistryRole === REGISTRY_ROLES.COLLABORATOR
  const showPlainBackground =
    SHOW_PLAIN_BACKGROUND_PATHS.includes(currentPathname)
  const showBackground = SHOW_BACKGROUND_PATHS.includes(currentPathname)
  const showLoginLink = currentPathname === PATHS.ROAD_AHEAD
  const showHeader = !HIDE_HEADER_PATHS.includes(currentPathname)

  const registryLandingPath =
    registry?.id && isCollaboratorActivation
      ? registryFromIdPath(registry.id)
      : baseRegistryHomePath

  const getPostSignupPath = () => {
    if (isInActEx2bMobileVariation) {
      return PATHS.INSTALL_APP
    }
    return PATHS.ADD_PARTNER
  }

  const getAddItemsPath = () => {
    if (deviceSupportsBabylistApp) {
      return PATHS.INSTALL_APP
    }
    if (showExtensionPage) {
      return PATHS.INSTALL_EXTENSION
    }
    return PATHS.ADD_BUTTON
  }

  const logoImage = (
    <Image
      alt="babylist-logo"
      src="https://images.babylist.com/image/upload/f_auto,q_auto/v1690320329/Onboarding/Ex5_Babylist_logo.png"
      className={css.OnboardingWizard__logoImage}
    />
  )

  const addButtonOrExtension = isInDayZeroDesktopExtensionExperiment ? (
    <AddChromeExtension isOnboarding nextPath={registryLandingPath} />
  ) : (
    <AddButton isOnboarding nextPath={registryLandingPath} />
  )

  return (
    <>
      <div className={css.OnboardingWizard__progressBarContainer}>
        <div
          className={classNames(
            css.OnboardingWizard__progressBarBar,
            css[`OnboardingWizard__progressBarStep${currentOnboardingStep}`]
          )}
        />
      </div>
      <div
        className={classNames(css.OnboardingWizard__onboardingPlainBackground, {
          [css['OnboardingWizard__onboardingPlainBackground--hidden']]:
            !showPlainBackground,
        })}
      />
      <div
        className={classNames(css.OnboardingWizard__onboardingBackground, {
          [css['OnboardingWizard__onboardingBackground--hidden']]:
            !showBackground,
        })}
      />
      <Loader>
        <div className="position-rel">
          <Router basename={PATHS.BASE} location={{ pathname: initialPath }}>
            <CompatRouter>
              {/* this route is necessary in order for Switch to use location via props
                instead of context, which is necessary for the page transitions.
                see: https://tylermcginnis.com/react-router-animated-transitions */}
              <Routes>
                <Route
                  path="*"
                  Component={() => {
                    const navigationType = useNavigationType()
                    const location = useLocation()
                    if (location.pathname !== currentPathname) {
                      setCurrentPathname(location.pathname)
                    }
                    return (
                      <SlideTransition
                        enable={navigationType !== 'REPLACE'}
                        back={navigationType === 'POP'}
                      >
                        <div
                          className={classNames(
                            css.OnboardingWizard__onboarding
                          )}
                          key={location.key}
                        >
                          <div>
                            {showHeader && (
                              <div className={css.OnboardingWizard__header}>
                                {logoImage}
                              </div>
                            )}
                            <ScrollToTop>
                              <Routes location={location}>
                                <Route
                                  path={PATHS.FIRST_TIME_PARENT}
                                  element={
                                    <FirstTimeParentSlide
                                      nextPath={PATHS.SENTIMENT}
                                    />
                                  }
                                />
                                <Route
                                  path={PATHS.SENTIMENT}
                                  element={
                                    <Sentiment nextPath={PATHS.SIGNUP} />
                                  }
                                />
                                <Route
                                  path={PATHS.SIGNUP}
                                  element={
                                    <Signup
                                      nextPath={getPostSignupPath()}
                                      ssoEnabled={ssoEnabled}
                                    />
                                  }
                                />
                                <Route
                                  path={PATHS.ACCEPT_INVITE}
                                  element={
                                    <>
                                      {!skipPassword ? (
                                        <AcceptPartnerInvite
                                          /* eslint-disable-next-line react/destructuring-assignment */
                                          authToken={authToken}
                                          nextPath={getAddItemsPath()}
                                        />
                                      ) : (
                                        <Navigate to={getAddItemsPath()} />
                                      )}
                                    </>
                                  }
                                />
                                <Route
                                  path={PATHS.ADD_PARTNER}
                                  element={
                                    <AddPartner nextPath={getAddItemsPath()} />
                                  }
                                />
                                <Route
                                  path={PATHS.ACT_EX1_ONBOARDING_PRIMER}
                                  element={
                                    <AddExperiencePrimer
                                      nextPath={PATHS.ADD_ITEMS_EXPERIENCE}
                                    />
                                  }
                                />
                                <Route
                                  path={PATHS.ADD_ITEMS_VALUE_PROPOSITION}
                                  element={
                                    <AddItemsValueProposition
                                      nextPath={getAddItemsPath()}
                                    />
                                  }
                                />
                                <Route
                                  path={PATHS.ADD_ITEMS_EXPERIENCE}
                                  element={
                                    <MobileAddItemsExperience
                                      nextPath={PATHS.INSTALL_APP}
                                    />
                                  }
                                />
                                <Route
                                  path={PATHS.ADD_BUTTON}
                                  element={addButtonOrExtension}
                                />
                                <Route
                                  path={PATHS.INSTALL_APP}
                                  element={
                                    <InstallApp
                                      actEx2bMobile={actEx2bMobile}
                                      isCollaborator={isCollaboratorActivation}
                                      registrantNames={registry?.firstNames}
                                    />
                                  }
                                />
                                <Route
                                  path={PATHS.INSTALL_EXTENSION}
                                  element={<InstallExtension />}
                                />
                                <Route
                                  path={PATHS.POST_INSTALL_EXTENSION}
                                  element={
                                    <PostInstallExtension
                                      nextPath={registryLandingPath}
                                    />
                                  }
                                />
                                <Route
                                  path={'*'}
                                  element={
                                    /* @ts-ignore */
                                    <PathToParenthood
                                      nextPath={PATHS.FIRST_TIME_PARENT}
                                      onboardingFlow={onboardingFlow}
                                    />
                                  }
                                />
                              </Routes>
                            </ScrollToTop>
                          </div>
                          <div
                            className={classNames(
                              css.OnboardingWizard__loginPrompt,
                              {
                                [css['OnboardingWizard__loginPrompt--hidden']]:
                                  !showLoginLink,
                              }
                            )}
                          >
                            Already have an account?{' '}
                            <a
                              className={css.OnboardingWizard__loginLink}
                              href={loginPath}
                            >
                              Log In
                            </a>
                          </div>
                        </div>
                      </SlideTransition>
                    )
                  }}
                />
              </Routes>
            </CompatRouter>
          </Router>
        </div>
      </Loader>
    </>
  )
}

export default OnboardingWizard
